import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Phone, Building, Layers, MapPin, Save, ArrowLeft,
  CreditCard, FileText, Bot, Settings, ChevronRight, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserSettings() {
  const { user, setUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    name: '',
    whatsapp: '',
    company: '',
    floor: '',
    work_area: ''
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
      return;
    }
    
    if (user) {
      setProfileForm({
        name: user.name || '',
        whatsapp: user.whatsapp || '',
        company: user.company || '',
        floor: user.floor || '',
        work_area: user.work_area || ''
      });
    }
  }, [user, authLoading, navigate]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/profile/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileForm)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        toast.success('Perfil actualizado correctamente');
      } else {
        throw new Error('Error al actualizar');
      }
    } catch (error) {
      toast.error('Error al guardar los cambios');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const menuItems = [
    { id: 'profile', label: 'Mi Perfil', icon: User },
    { id: 'recurring-orders', label: 'Pedidos Recurrentes', icon: RefreshCw, href: '/settings/recurring-orders' },
    { id: 'billing', label: 'Facturación', icon: FileText, href: '/settings/billing' },
    { id: 'payment-methods', label: 'Métodos de Pago', icon: CreditCard, href: '/settings/payment-methods' },
    { id: 'nutrition', label: 'Agente de Nutrición', icon: Bot, href: '/settings/nutrition' },
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="user-settings">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">Configuración</h1>
              <p className="text-stone-500">Administra tu cuenta y preferencias</p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="md:col-span-1">
              <nav className="bg-white rounded-2xl border border-stone-200 p-2 space-y-1">
                {menuItems.map(item => (
                  item.href ? (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.href)}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-stone-600 hover:bg-stone-50 transition-colors"
                      data-testid={`settings-nav-${item.id}`}
                    >
                      <span className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        {item.label}
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                        activeTab === item.id
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'text-stone-600 hover:bg-stone-50'
                      }`}
                      data-testid={`settings-nav-${item.id}`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </button>
                  )
                ))}
              </nav>
            </div>

            {/* Content */}
            <div className="md:col-span-3">
              {activeTab === 'profile' && (
                <div className="bg-white rounded-2xl border border-stone-200 p-6">
                  <h2 className="text-lg font-semibold text-stone-900 mb-6">Información Personal</h2>
                  
                  <div className="space-y-5">
                    {/* Profile Picture */}
                    <div className="flex items-center gap-4 pb-5 border-b border-stone-100">
                      <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-10 h-10 text-emerald-700" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{user.email}</p>
                        <p className="text-sm text-stone-500">Conectado con Google</p>
                      </div>
                    </div>

                    {/* Name */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                        <User className="w-4 h-4" />
                        Nombre
                      </label>
                      <Input
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                        className="bg-stone-50 border-stone-200 rounded-xl h-12"
                        data-testid="profile-name-input"
                      />
                    </div>

                    {/* WhatsApp */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                        <Phone className="w-4 h-4" />
                        WhatsApp
                      </label>
                      <Input
                        type="tel"
                        value={profileForm.whatsapp}
                        onChange={(e) => setProfileForm({...profileForm, whatsapp: e.target.value})}
                        className="bg-stone-50 border-stone-200 rounded-xl h-12"
                        data-testid="profile-whatsapp-input"
                      />
                    </div>

                    {/* Company */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                        <Building className="w-4 h-4" />
                        Empresa
                      </label>
                      <Input
                        value={profileForm.company}
                        onChange={(e) => setProfileForm({...profileForm, company: e.target.value})}
                        className="bg-stone-50 border-stone-200 rounded-xl h-12"
                        data-testid="profile-company-input"
                      />
                    </div>

                    {/* Floor */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                        <Layers className="w-4 h-4" />
                        Piso
                      </label>
                      <Input
                        value={profileForm.floor}
                        onChange={(e) => setProfileForm({...profileForm, floor: e.target.value})}
                        className="bg-stone-50 border-stone-200 rounded-xl h-12"
                        data-testid="profile-floor-input"
                      />
                    </div>

                    {/* Work Area */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                        <MapPin className="w-4 h-4" />
                        Zona de Trabajo
                      </label>
                      <Input
                        value={profileForm.work_area}
                        onChange={(e) => setProfileForm({...profileForm, work_area: e.target.value})}
                        className="bg-stone-50 border-stone-200 rounded-xl h-12"
                        data-testid="profile-workarea-input"
                      />
                    </div>

                    <Button
                      className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full py-6 mt-4"
                      onClick={handleSaveProfile}
                      disabled={loading}
                      data-testid="save-profile-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                          Guardando...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save className="w-5 h-5" />
                          Guardar Cambios
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
